import {
  AUDIT_MAX_ARRAY_ITEMS,
  AUDIT_MAX_OBJECT_KEYS,
  AUDIT_MAX_STRING_LENGTH,
  AUDIT_SENSITIVE_KEYS,
} from '@/services/audit/constants';

function isSensitiveKey(key: string): boolean {
  const lower = key.toLowerCase();
  return AUDIT_SENSITIVE_KEYS.some(
    (s) => lower === s.toLowerCase() || lower.includes(s.toLowerCase()),
  );
}

function truncateString(value: string): string {
  if (value.length <= AUDIT_MAX_STRING_LENGTH) return value;
  return `${value.slice(0, AUDIT_MAX_STRING_LENGTH)}…`;
}

function serializeValue(value: unknown, depth: number): unknown {
  if (value === null || value === undefined) return value;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'string') return truncateString(value);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value)) {
    const slice = value.slice(0, AUDIT_MAX_ARRAY_ITEMS);
    if (value.length > AUDIT_MAX_ARRAY_ITEMS) {
      return [
        ...slice.map((item) => serializeValue(item, depth + 1)),
        `[+${value.length - AUDIT_MAX_ARRAY_ITEMS} items]`,
      ];
    }
    return slice.map((item) => serializeValue(item, depth + 1));
  }
  if (typeof value === 'object' && depth < 4) {
    const obj = value as Record<string, unknown>;
    const entries = Object.entries(obj).slice(0, AUDIT_MAX_OBJECT_KEYS);
    const out: Record<string, unknown> = {};
    for (const [k, v] of entries) {
      if (isSensitiveKey(k)) {
        out[k] = '[redacted]';
        continue;
      }
      out[k] = serializeValue(v, depth + 1);
    }
    if (Object.keys(obj).length > AUDIT_MAX_OBJECT_KEYS) {
      out._truncated = true;
    }
    return out;
  }
  return '[unsupported]';
}

/** Prepara un objeto para persistir en auditLogs (sin secretos ni blobs grandes). */
export function sanitizeSnapshot(
  value: Record<string, unknown> | object | null | undefined,
): Record<string, unknown> | undefined {
  if (value === null || value === undefined) return undefined;
  const raw =
    typeof value === 'object' && !Array.isArray(value)
      ? ({ ...value } as Record<string, unknown>)
      : ({} as Record<string, unknown>);

  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(raw)) {
    if (isSensitiveKey(key)) {
      out[key] = '[redacted]';
      continue;
    }
    out[key] = serializeValue(val, 0);
  }
  return out;
}

/** Convierte entidades de dominio (con Date) a snapshot auditable. */
export function toAuditSnapshot(
  entity: object,
  omitKeys: readonly string[] = [],
): Record<string, unknown> {
  const omit = new Set(omitKeys);
  const raw: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(entity)) {
    if (omit.has(key)) continue;
    if (val instanceof Date) {
      raw[key] = val.toISOString();
    } else {
      raw[key] = val;
    }
  }
  return sanitizeSnapshot(raw) ?? {};
}
